import { XIcon } from 'lucide-react'

import { cn } from '~/utils/classnames'

import { Modal } from './modal'

type DialogProps = React.ComponentProps<typeof Modal>

const DialogRoot = ({ ...props }: DialogProps) => {
  return <Modal {...props} />
}

interface DialogContentProps extends React.ComponentProps<
  typeof Modal.Content
> {
  align?: 'center' | 'top'
}

const DialogContent = ({
  className,
  children,
  align = 'center',
  ...props
}: DialogContentProps) => {
  return (
    <Modal.Content
      className={cn(
        'bg-background text-foreground border-border/4 m-auto w-full max-w-[min(480px,calc(100vw-var(--spacing)*4))] rounded-2xl border shadow-lg',
        'max-h-[calc(100svh-2rem)] overflow-y-auto backdrop:bg-black/20 backdrop:backdrop-blur-sm',
        'transition-all duration-300 backdrop:transition-all motion-reduce:transition-none motion-reduce:backdrop:transition-none',
        'not-data-[status=open]:translate-y-2 not-data-[status=open]:scale-95 not-data-[status=open]:opacity-0 not-data-[status=open]:duration-150 not-data-[status=open]:backdrop:opacity-0',
        align === 'top' && 'mt-4',
        className
      )}
      {...props}
    >
      {children}
    </Modal.Content>
  )
}

const DialogTrigger = Modal.Trigger

const DialogClose = Modal.Close

const DialogTitle = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Modal.Title>) => {
  return (
    <Modal.Title
      className={cn(
        'border-border/4 flex items-center justify-between gap-2 border-b px-3 py-2.5 text-sm font-medium',
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2">{children}</div>
      <DialogClose className="cursor-pointer rounded-full text-base">
        <XIcon />
      </DialogClose>
    </Modal.Title>
  )
}

const DialogDescription = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Modal.Description>) => {
  return (
    <Modal.Description
      className={cn('p-3 text-sm font-medium', className)}
      {...props}
    >
      {children}
    </Modal.Description>
  )
}

const DialogActions = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<'div'>) => (
  <div
    className={cn(
      'flex flex-col gap-2 p-3 pt-2 sm:flex-row sm:justify-start',
      className
    )}
    {...props}
  >
    {children}
  </div>
)

const Dialog = Object.assign(DialogRoot, {
  Content: DialogContent,
  Trigger: DialogTrigger,
  Close: DialogClose,
  Title: DialogTitle,
  Description: DialogDescription,
  Actions: DialogActions,
})

export { Dialog }
